import { useState, useEffect, useCallback } from 'react';
import {
  isInstallPromptAvailable,
  isStandaloneMode,
  isInstallDismissed,
  dismissInstallPrompt,
  promptPwaInstall,
  subscribeInstallPrompt,
} from './pwaService';

export interface UsePwaInstallReturn {
  canInstall: boolean;
  isStandalone: boolean;
  isDismissed: boolean;
  install: () => Promise<boolean>;
  dismiss: () => void;
}

export function usePwaInstall(): UsePwaInstallReturn {
  const [canInstall, setCanInstall] = useState<boolean>(() => isInstallPromptAvailable());
  const [isStandalone, setIsStandalone] = useState<boolean>(() => isStandaloneMode());
  const [isDismissed, setIsDismissed] = useState<boolean>(() => isInstallDismissed());

  useEffect(() => {
    setIsStandalone(isStandaloneMode());
    setIsDismissed(isInstallDismissed());

    const unsubscribe = subscribeInstallPrompt((available) => {
      setCanInstall(available);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const install = useCallback(async () => {
    const success = await promptPwaInstall();
    if (!success) {
      // If user declined or cancelled the browser dialog, don't show the banner again immediately
      dismissInstallPrompt();
      setIsDismissed(true);
    }
    return success;
  }, []);

  const dismiss = useCallback(() => {
    dismissInstallPrompt();
    setIsDismissed(true);
  }, []);

  return {
    canInstall: canInstall && !isStandalone && !isDismissed,
    isStandalone,
    isDismissed,
    install,
    dismiss,
  };
}
